import { paintCorrectionPage, paintCorrectionFocus } from '../../data/paint-correction'
import { paintCorrectionBeforeAfter } from '../../data/lyle-media'
import { getServicePageJsonLd } from '../../lib/seo'
import { PageShell } from '../layout/page-shell'
import { PageTitle } from '../seo/page-title'
import { ButtonLink } from '../ui/button'

export interface PaintCorrectionPageProps {}

export function PaintCorrectionPage(_: Readonly<PaintCorrectionPageProps>) {
  const jsonLd = getServicePageJsonLd('paintCorrection')

  return (
    <PageShell>
      <PageTitle title="Paint Correction | Bellarine & Geelong | Autoglow" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <section className="packages-hero">
        <img
          src="/images/paint-correction.jpg"
          alt="A professional detailer machine polishing a dark vehicle panel."
          width="2560"
          height="1440"
        />
        <div className="packages-hero-content">
          <p className="eyebrow">{paintCorrectionPage.eyebrow}</p>
          <h1>
            {paintCorrectionPage.title} <span>{paintCorrectionPage.titleHighlight}</span>
          </h1>
          <p>{paintCorrectionPage.intro}</p>
          <div>
            <ButtonLink href={paintCorrectionPage.quoteHref}>Request a tailored quote</ButtonLink>
            <ButtonLink href="/ceramic-protection" variant="ghost">Ceramic Protection</ButtonLink>
          </div>
        </div>
      </section>

      <section className="detailing-section" aria-labelledby="paint-correction-what">
        <div className="section-container">
          <div className="split-section-heading">
            <div>
              <p className="eyebrow">Studio work</p>
              <h2 id="paint-correction-what">What paint correction is</h2>
            </div>
            <div>
              <p>{paintCorrectionPage.whatItIs}</p>
              <p>{paintCorrectionPage.whoItSuits}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="packages-list-section" id="paint-correction-focus">
        <div className="section-container">
          <div className="page-section-heading">
            <h2>What it is for</h2>
            <p>{paintCorrectionPage.priceNote}</p>
          </div>
          <div className="package-card-grid">
            {paintCorrectionFocus.map((item) => (
              <article className="package-card" key={item.id}>
                <h3>{item.name}</h3>
                <p className="paint-level-copy">{item.summary}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="detailing-section" aria-labelledby="paint-correction-issues">
        <div className="section-container">
          <div className="page-section-heading">
            <h2 id="paint-correction-issues">{paintCorrectionPage.issuesHeading}</h2>
            <p>{paintCorrectionPage.caveat}</p>
          </div>
          <ul className="paint-correction-issues">
            {paintCorrectionPage.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
          <div>
            <ButtonLink href={paintCorrectionPage.quoteHref}>Request a tailored quote</ButtonLink>
          </div>
        </div>
      </section>

      <section className="results-gallery" aria-labelledby="correction-results-heading">
        <div className="section-container">
          <div className="center-heading">
            <p className="eyebrow">Studio Results</p>
            <h2 id="correction-results-heading">Before and after</h2>
            <p>
              Placeholder pairs until Lyle’s approved before and after photos are loaded into the
              studio media folder. Captions are not assigned to a specific vehicle.
            </p>
          </div>
          <div className="before-after-grid">
            {paintCorrectionBeforeAfter.map((pair) => (
              <figure className="before-after-pair" key={pair.caption}>
                <div>
                  <img src={pair.before} alt={pair.beforeAlt} width="900" height="620" loading="lazy" />
                  <figcaption>Before</figcaption>
                </div>
                <div>
                  <img src={pair.after} alt={pair.afterAlt} width="900" height="620" loading="lazy" />
                  <figcaption>After</figcaption>
                </div>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="detailing-teaser-section" id="protect-the-finish" aria-labelledby="protect-finish-heading">
        <div className="section-container detailing-teaser-grid">
          <img
            src="/images/page-assets/packages-hero.jpg"
            alt="Ceramic-coated vehicle paint with water beading in the Autoglow studio."
            width="1376"
            height="768"
            loading="lazy"
          />
          <div>
            <p className="eyebrow">Paint Correction + Ceramic Protection</p>
            <h2 id="protect-finish-heading">{paintCorrectionPage.protectionHeading}</h2>
            <p>{paintCorrectionPage.protectionCopy}</p>
            <ul>
              <li>GYEON 1, 3 and 5 year coatings</li>
              <li>Matched after inspection</li>
              <li>Installed in the Drysdale studio</li>
            </ul>
            <ButtonLink href="/ceramic-protection" variant="solid">Explore Ceramic Protection</ButtonLink>
          </div>
        </div>
      </section>
    </PageShell>
  )
}

export default PaintCorrectionPage
